import { useMemo, useState } from "react";
import { Alert, Breadcrumb } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { InlineLoadingIcon } from "../../components/InlineLoadingIcon/InlineLoadingIcon";
import LoadingMask from "../../components/LoadingMask/LoadingMask";
import SyncStatusBar from "../../components/SyncStatusBar/SyncStatusBar";
import { useCustomerClusters } from "../../hooks/use-customer-clusters";
import { useDocumentTitle } from "../../hooks/use-document-title";
import { usePaymentCountryClusters } from "../../hooks/use-payment-country-clusters";
import { useProject } from "../../hooks/use-project";
import { useProjectCharges } from "../../hooks/use-project-charges";
import { useStripeConnectInfo } from "../../hooks/use-stripe-connect-info";
import { i18nAmount } from "../../lib/i18n";
import { CustomerCluster } from "../../types/CustomerCluster";
import ClusterDetailSidebar from "./ClusterDetailSidebar";
import "./JortsPage.scss";

function formatCurrency(
  cents: number,
  currency: string,
  locale = "en-US",
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

const COUNTRY_LOCALE: Record<string, string> = {
  US: "en-US",
  GB: "en-GB",
  AU: "en-AU",
  CA: "en-CA",
  NZ: "en-NZ",
  IE: "en-IE",
  ZA: "en-ZA",
  SG: "en-SG",
  HK: "en-HK",
  IN: "en-IN",
  DE: "de-DE",
  AT: "de-AT",
  CH: "de-CH",
  FR: "fr-FR",
  BE: "fr-BE",
  ES: "es-ES",
  MX: "es-MX",
  AR: "es-AR",
  CO: "es-CO",
  CL: "es-CL",
  PE: "es-PE",
  IT: "it-IT",
  PT: "pt-PT",
  BR: "pt-BR",
  NL: "nl-NL",
  SE: "sv-SE",
  NO: "nb-NO",
  DK: "da-DK",
  FI: "fi-FI",
  PL: "pl-PL",
  CZ: "cs-CZ",
  RO: "ro-RO",
  HU: "hu-HU",
  GR: "el-GR",
  TR: "tr-TR",
  IL: "he-IL",
  AE: "ar-AE",
  SA: "ar-SA",
  EG: "ar-EG",
  JP: "ja-JP",
  CN: "zh-CN",
  TW: "zh-TW",
  KR: "ko-KR",
  TH: "th-TH",
  VN: "vi-VN",
  ID: "id-ID",
  MY: "ms-MY",
  PH: "en-PH",
  RU: "ru-RU",
  UA: "uk-UA",
  NG: "en-NG",
  KE: "en-KE",
};

const COUNTRY_CURRENCY: Record<string, string> = {
  US: "USD",
  GB: "GBP",
  AU: "AUD",
  CA: "CAD",
  NZ: "NZD",
  IE: "EUR",
  ZA: "ZAR",
  SG: "SGD",
  HK: "HKD",
  IN: "INR",
  DE: "EUR",
  AT: "EUR",
  CH: "CHF",
  FR: "EUR",
  BE: "EUR",
  ES: "EUR",
  MX: "MXN",
  AR: "ARS",
  CO: "COP",
  CL: "CLP",
  PE: "PEN",
  IT: "EUR",
  PT: "EUR",
  BR: "BRL",
  NL: "EUR",
  SE: "SEK",
  NO: "NOK",
  DK: "DKK",
  FI: "EUR",
  PL: "PLN",
  CZ: "CZK",
  RO: "RON",
  HU: "HUF",
  GR: "EUR",
  TR: "TRY",
  IL: "ILS",
  AE: "AED",
  SA: "SAR",
  EG: "EGP",
  JP: "JPY",
  CN: "CNY",
  TW: "TWD",
  KR: "KRW",
  TH: "THB",
  VN: "VND",
  ID: "IDR",
  MY: "MYR",
  PH: "PHP",
  RU: "RUB",
  UA: "UAH",
  NG: "NGN",
  KE: "KES",
};

function getLocaleForCountry(countryCode: string): string {
  return COUNTRY_LOCALE[countryCode.toUpperCase()] ?? "en-US";
}

function getLocalCurrency(countryCode: string): string | null {
  return COUNTRY_CURRENCY[countryCode.toUpperCase()] ?? null;
}

function sortedCurrencyEntries(
  totalAmountByCurrency: Record<string, number>,
  countryCode: string,
): [string, number][] {
  const localCurrency = getLocalCurrency(countryCode);
  return Object.entries(totalAmountByCurrency).sort(([a], [b]) => {
    const aIsLocal = a.toUpperCase() === localCurrency;
    const bIsLocal = b.toUpperCase() === localCurrency;
    if (aIsLocal && !bIsLocal) return -1;
    if (!aIsLocal && bIsLocal) return 1;
    return 0;
  });
}

const countryNames = new Intl.DisplayNames(["en"], { type: "region" });

function formatCountryCode(code: string): string {
  try {
    return countryNames.of(code) ?? code;
  } catch {
    return code;
  }
}

const JortsPage: React.FC = () => {
  useDocumentTitle("Business Insights");

  const { projectId } = useParams();
  const { project, isLoading } = useProject(projectId ?? null);
  const { stripeConnectInfo } = useStripeConnectInfo(projectId ?? null);
  const { charges } = useProjectCharges(projectId ?? null);
  const { clusters, isLoading: clustersLoading } = useCustomerClusters(
    projectId ?? null,
  );
  const {
    clusters: countryClusters,
    isLoading: countryLoading,
  } = usePaymentCountryClusters(projectId ?? null);
  const [selectedCluster, setSelectedCluster] =
    useState<CustomerCluster | null>(null);

  const clusterCharges = useMemo(() => {
    if (!selectedCluster) return [];
    const emailSet = new Set(
      selectedCluster.customerEmails.map((e) => e.toLowerCase()),
    );
    return charges.filter(
      (c) => c.customerEmail && emailSet.has(c.customerEmail.toLowerCase()),
    );
  }, [selectedCluster, charges]);

  // Group clusters by type for display
  const groupedClusters = useMemo(() => {
    const education: CustomerCluster[] = [];
    const corporate: CustomerCluster[] = [];
    const individual: CustomerCluster[] = [];

    for (const cluster of clusters) {
      switch (cluster.clusterType) {
        case "education":
          education.push(cluster);
          break;
        case "corporate":
          corporate.push(cluster);
          break;
        case "individual":
          individual.push(cluster);
          break;
      }
    }

    return { education, corporate, individual };
  }, [clusters]);

  const sortedCountryClusters = useMemo(
    () =>
      [...countryClusters].sort((a, b) => b.chargeCount - a.chargeCount),
    [countryClusters],
  );

  if (isLoading) {
    return <LoadingMask message="Loading project..." />;
  }

  return (
    <div className="jorts-page">
      <Breadcrumb>
        <Breadcrumb.Item href={`/project/${projectId}`}>
          {project?.name ?? "Project"}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Business Insights</Breadcrumb.Item>
      </Breadcrumb>
      <div className="page-header">
        Business Insights for{" "}
        <span className="text-highlight">{project?.name}</span>
      </div>

      <SyncStatusBar
        stripeConnectInfo={stripeConnectInfo}
        projectId={projectId}
      />

      {/* Customer Clusters */}
      <div className="clusters-section">
        <h5>Customer Clusters</h5>
        {clustersLoading ? (
          <InlineLoadingIcon />
        ) : clusters.length === 0 ? (
          <Alert variant="info">
            No customer clusters yet. Sync charges to generate clusters.
          </Alert>
        ) : (
          <>
            {groupedClusters.education.length > 0 && (
              <ClusterGroup
                title="Education"
                clusters={groupedClusters.education}
                selectedCluster={selectedCluster}
                onSelect={setSelectedCluster}
              />
            )}
            {groupedClusters.corporate.length > 0 && (
              <ClusterGroup
                title="Corporate"
                clusters={groupedClusters.corporate}
                selectedCluster={selectedCluster}
                onSelect={setSelectedCluster}
              />
            )}
            {groupedClusters.individual.length > 0 && (
              <ClusterGroup
                title="Individual"
                clusters={groupedClusters.individual}
                selectedCluster={selectedCluster}
                onSelect={setSelectedCluster}
              />
            )}
          </>
        )}
      </div>

      {selectedCluster && (
        <ClusterDetailSidebar
          cluster={selectedCluster}
          charges={clusterCharges}
          onClose={() => setSelectedCluster(null)}
        />
      )}
      {/* Country Clusters */}
      <div className="clusters-section">
        <h5>Payment Countries</h5>
        {countryLoading ? (
          <InlineLoadingIcon />
        ) : sortedCountryClusters.length === 0 ? (
          <Alert variant="info">
            No country data yet. Sync charges to generate country clusters.
          </Alert>
        ) : (
          <div className="cluster-cards">
            {sortedCountryClusters.map((cc) => (
              <div key={cc.id} className="cluster-card">
                <div className="cluster-card-domain">
                  {formatCountryCode(cc.country)}
                </div>
                <div className="cluster-card-stats">
                  <span>
                    {i18nAmount(cc.customerEmails.length, {
                      singular: "customer",
                      plural: "customers",
                    })}
                  </span>
                  <span>
                    {i18nAmount(cc.chargeCount, {
                      singular: "charge",
                      plural: "charges",
                    })}
                  </span>
                  {sortedCurrencyEntries(
                    cc.totalAmountByCurrency,
                    cc.country,
                  ).map(([currency, amount]) => (
                    <span key={currency}>
                      {formatCurrency(
                        amount,
                        currency,
                        getLocaleForCountry(cc.country),
                      )}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface ClusterGroupProps {
  title: string;
  clusters: CustomerCluster[];
  selectedCluster: CustomerCluster | null;
  onSelect: (cluster: CustomerCluster) => void;
}

const ClusterGroup: React.FC<ClusterGroupProps> = ({
  title,
  clusters,
  selectedCluster,
  onSelect,
}) => (
  <div className="cluster-group">
    <h6>{title}</h6>
    <div className="cluster-cards">
      {clusters.map((cluster) => (
        <div
          key={cluster.id}
          className={`cluster-card ${selectedCluster?.id === cluster.id ? "selected" : ""}`}
          onClick={() => onSelect(cluster)}
        >
          <div className="cluster-card-domain">{cluster.domain}</div>
          <div className="cluster-card-stats">
            <span>
              {i18nAmount(cluster.customerEmails.length, {
                singular: "customer",
                plural: "customers",
              })}
            </span>
            <span>
              {i18nAmount(cluster.chargeCount, {
                singular: "charge",
                plural: "charges",
              })}
            </span>
            {Object.entries(cluster.totalAmountByCurrency).map(
              ([currency, amount]) => (
                <span key={currency}>{formatCurrency(amount, currency)}</span>
              ),
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default JortsPage;
